import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import { withStyles } from '@material-ui/core/styles';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogActions from '@material-ui/core/DialogActions';
import { Box } from '@material-ui/core';
import Validation from '../../common/Validation';
import BusinessCard from '../../components/BusinessCard';
import PRODUCT_DIALOG_MODE from '../ProductManagement/Consts';
import Button from '../../components/Button/CustomButton';
import {
    ClearGetExchangeBusinessCard, clearGetUserProfile,
    GetExchangeBusinessCardRequested,
    getUserProfileRequested,
} from '../../actions/ProductListOnDisplay';

const defaultBusinessCardParams = {
    work_place: '',
    department: '',
    position: '',
    first_name: '',
    last_name: '',
    zip_code: '',
    address: '',
    phone_number: '',
    fax_number: '',
    email_public: '',
    email_notifications: '',
};
const MAX_CHAR_LENGTH = 255;
const MAX_ADDRESS_CHAR_LENGTH = 1000;

const DialogActions = withStyles((theme) => ({
    root: {
        margin: 0,
        padding: theme.spacing(1),
    },
}))(MuiDialogActions);

const DialogContent = withStyles((theme) => ({
    root: {
        padding: theme.spacing(2),
    },
}))(MuiDialogContent);

const BusinessCardExchange = ({
    handleClose,
    productId,
    sendBusinessCard,
    getUserProfile,
    userProfile,
    getExchangeBusinessCard,
    exchangeBusinessCardData,
    clearExChangeBusinessCard,
    clearUserProfile,
    isAuthorized,
    isWeakAuth,
    customColorExhibitor,
    customTextPersonInfo,
    locale,
}) => {
    const [validateResults, setValidateResults] = useState({});
    const [businessCardParams, setBusinessCardParams] = useState(defaultBusinessCardParams);
    const intl = useIntl();

    const handleSetBusinessCardParams = (field, value) => {
        setBusinessCardParams((params) => ({ ...params, [field]: value }));
    };

    useEffect(() => () => {
        clearExChangeBusinessCard();
        clearUserProfile();
    }, [clearExChangeBusinessCard, clearUserProfile]);

    useEffect(() => {
        if (!isAuthorized || isWeakAuth) return;

        if (_.isEmpty(exchangeBusinessCardData) && _.isEmpty(userProfile)) {
            localStorage.removeItem('businessCardParams');
            getUserProfile();
            getExchangeBusinessCard();
        }
    }, [clearExChangeBusinessCard, clearUserProfile, exchangeBusinessCardData, getExchangeBusinessCard, getUserProfile, userProfile, isAuthorized, isWeakAuth]);

    useEffect(() => {
        const localTemp = JSON.parse(localStorage.getItem('businessCardParams'));

        if ((!isAuthorized || isWeakAuth) && localTemp) {
            setBusinessCardParams((prevState) => ({
                ...prevState,
                work_place: localTemp.work_place,
                department: localTemp.department,
                position: localTemp.position,
                first_name: localTemp.first_name,
                last_name: localTemp.last_name,
                zip_code: localTemp.zip_code,
                address: localTemp.address,
                phone_number: localTemp.phone_number,
                fax_number: localTemp.fax_number,
                email_public: localTemp.email_public,
                email_notifications: localTemp.email_notifications,
            }));

            return;
        }

        if (_.isEmpty(exchangeBusinessCardData) && _.isEmpty(userProfile)) {
            return;
        }

        if (!_.isEmpty(exchangeBusinessCardData)) {
            setBusinessCardParams((prevState) => ({
                ...prevState,
                work_place: exchangeBusinessCardData.work_place,
                department: exchangeBusinessCardData.department,
                position: exchangeBusinessCardData.position,
                first_name: exchangeBusinessCardData.first_name,
                last_name: exchangeBusinessCardData.last_name,
                zip_code: exchangeBusinessCardData.zip_code,
                address: exchangeBusinessCardData.address,
                phone_number: exchangeBusinessCardData.phone_number,
                fax_number: exchangeBusinessCardData.fax_number,
                email_public: exchangeBusinessCardData.email_public,
                email_notifications: exchangeBusinessCardData.email_notifications,
            }));

            return;
        }

        if (!_.isEmpty(userProfile)) {
            setBusinessCardParams((prevState) => ({
                ...prevState,
                work_place: userProfile.work_place,
                department: userProfile.department,
                position: userProfile.position,
                first_name: userProfile.first_name,
                last_name: userProfile.last_name,
                zip_code: userProfile.zip_code,
                address: userProfile.address,
                phone_number: userProfile.phone_number,
                fax_number: userProfile.fax_number,
                email_public: userProfile.email,
            }));
        }
    }, [exchangeBusinessCardData, userProfile, isAuthorized, isWeakAuth]);

    const validate = (values) => {
        const {
            work_place, department, position, first_name, last_name,
            zip_code, address, phone_number, fax_number, email_public, email_notifications,
        } = values;

        const validateBusinessCard = {
            work_place: [
                () => Validation.isNotEmpty(work_place),
                () => Validation.validateMaxLength(work_place, MAX_CHAR_LENGTH, typeof work_place),
            ],
            department: [
                () => Validation.validateMaxLength(department, MAX_CHAR_LENGTH, typeof department),
            ],
            position: [
                () => Validation.validateMaxLength(position, MAX_CHAR_LENGTH, typeof position),
            ],
            first_name: [
                () => Validation.isNotEmpty(first_name),
                () => Validation.validateMaxLength(first_name, MAX_CHAR_LENGTH, typeof first_name),
            ],
            last_name: [
                () => Validation.isNotEmpty(last_name),
                () => Validation.validateMaxLength(last_name, MAX_CHAR_LENGTH, typeof last_name),
            ],
            zip_code: [
                () => Validation.isNotEmpty(zip_code),
                () => Validation.validateMaxLength(zip_code, MAX_CHAR_LENGTH, typeof zip_code),
            ],
            address: [
                () => Validation.isNotEmpty(address),
                () => Validation.validateMaxLength(address, MAX_ADDRESS_CHAR_LENGTH, typeof address),
            ],
            phone_number: [
                () => Validation.isNotEmpty(phone_number),
                () => Validation.validatePhoneNumber(phone_number.trim()),
            ],
            fax_number: [
                () => Validation.validateMaxLength(fax_number, MAX_CHAR_LENGTH, typeof fax_number),
            ],
            email_public: [
                () => Validation.isNotEmpty(email_public),
                () => Validation.validateMaxLength(email_public, MAX_CHAR_LENGTH, typeof email_public),
                () => Validation.validateEmail(email_public),
            ],
            email_notifications: [
                () => Validation.isNotEmpty(email_notifications),
                () => Validation.validateMaxLength(email_notifications, MAX_CHAR_LENGTH, typeof email_notifications),
                () => Validation.validateEmail(email_notifications),
            ],
        };

        const validateResult = Validation.validateAllRules(validateBusinessCard);

        if (Object.keys(validateResult).length > 0) {
            setValidateResults({ ...validateResult });
            return false;
        }
        setValidateResults({});
        return true;
    };

    const handleSendBusinessCard = (e) => {
        e.stopPropagation();

        const isValid = validate(businessCardParams);

        if (isValid) {
            if (!isAuthorized || isWeakAuth) {
                localStorage.setItem('businessCardParams', JSON.stringify(businessCardParams));
            }

            sendBusinessCard({ productId, businessCardParams, language: locale });
        }
    };

    return (
        <>
            <DialogContent dividers>
                <BusinessCard
                    params={businessCardParams}
                    handleSetBusinessCardParams={handleSetBusinessCardParams}
                    MAX_CHAR_LENGTH={MAX_CHAR_LENGTH}
                    validateResults={validateResults}
                    mode={PRODUCT_DIALOG_MODE.UPDATE}
                    customColorExhibitor={customColorExhibitor}
                    customTextPersonInfo={customTextPersonInfo}
                />
            </DialogContent>

            <DialogActions>
                <Box style={{ margin: 'auto' }}>
                    <Button
                        autoFocus
                        gradient
                        size="md"
                        color="success"
                        style={{ width: '200px', fontSize: '20px', left: ' 20%' }}
                        onClick={handleSendBusinessCard}
                        buttonTextColor={customColorExhibitor?.button_text_color}
                    >
                        {intl.formatMessage({ id: 'saveCard' })}
                    </Button>
                    <Button
                        autoFocus
                        gradient
                        size="md"
                        color="danger"
                        style={{ width: '150px', marginRight: '10px', left: ' 80%' }}
                        onClick={handleClose}
                        buttonTextColor={customColorExhibitor?.button_text_color}
                    >
                        {intl.formatMessage({ id: 'cancel' })}
                    </Button>
                </Box>
            </DialogActions>
        </>
    );
};

BusinessCardExchange.propTypes = {
    handleClose: PropTypes.func.isRequired,
    productId: PropTypes.number.isRequired,
    sendBusinessCard: PropTypes.func.isRequired,
    getUserProfile: PropTypes.func.isRequired,
    userProfile: PropTypes.oneOfType([() => null, PropTypes.object]).isRequired,
    getExchangeBusinessCard: PropTypes.func.isRequired,
    exchangeBusinessCardData: PropTypes.oneOfType([() => null, PropTypes.object]).isRequired,
    clearExChangeBusinessCard: PropTypes.func.isRequired,
    clearUserProfile: PropTypes.func.isRequired,
    isAuthorized: PropTypes.bool.isRequired,
    isWeakAuth: PropTypes.bool.isRequired,
    customTextPersonInfo: PropTypes.oneOfType([() => null, PropTypes.object]).isRequired,
    customColorExhibitor: PropTypes.oneOfType([() => null, PropTypes.object]).isRequired,
    locale: PropTypes.string.isRequired,
};

const mapStateToProps = (state) => ({
    userProfile: state.productListOnDisplay.userProfile.data,
    exchangeBusinessCardData: state.productListOnDisplay.getBusinessCard.data,
    isWeakAuth: !!state.auth.credential.data?.isWeakAuth,
    locale: state.locale.lang.current,
});

const mapDispatchToProps = (dispatch) => ({
    getUserProfile: () => {
        dispatch(getUserProfileRequested());
    },
    getExchangeBusinessCard: () => {
        dispatch(GetExchangeBusinessCardRequested());
    },
    clearExChangeBusinessCard: () => {
        dispatch(ClearGetExchangeBusinessCard());
    },
    clearUserProfile: () => {
        dispatch(clearGetUserProfile());
    },
});

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(BusinessCardExchange);
